import { isLorebookAttachBlocked, useApp, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import { Button, ButtonVariant, Chip, IconVariants, Image, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { Lorebook } from "@next-platform/packages-ts-types";
import { useCallback, useMemo, useState } from "react";

import { CapabilityShareLorebookModal, Col, LocalizedLink, PageLayout, PageSize, Row } from "@/components";
import AvatarWithModal from "@/components/common/AvatarWithModal";
import NavigateBackButtonV2 from "@/components/common/NavigateBackButtonV2";
import Trans from "@/components/Trans/Trans";
import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag, useMediaStore } from "@/hooks";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils";

import ModalChatbot from "./ModalChatbot";

type LorebookProfileHeaderProps = {
  lorebook: Lorebook;
};

type LorebookProfileActionsProps = {
  onShowShare: () => void;
  handleShowCharacterModal: () => void;
  hasApplyToChatbotFlag: boolean;
  isAttachBlocked: boolean;
};

function LorebookProfileActions({ handleShowCharacterModal, onShowShare, hasApplyToChatbotFlag, isAttachBlocked }: LorebookProfileActionsProps) {
  const { t } = useTranslation();
  // Keep the accessible name in step with the visible label so assistive tech doesn't
  // announce the enabled action on a disabled control.
  const pendingReviewLabel = t("lorebook:profile.pendingReview");

  return (
    <Row className="flex-wrap gap-1">
      {hasApplyToChatbotFlag ? (
        <Button
          iconStart={IconVariants.LINK}
          variant={ButtonVariant.CONTAINED}
          onClick={handleShowCharacterModal}
          disabled={isAttachBlocked}
          aria-label={isAttachBlocked ? pendingReviewLabel : "Attach Lorebook"}
        >
          {isAttachBlocked ? <Trans i18nKey="lorebook:profile.pendingReview" /> : <Trans i18nKey="lorebook:profile.attachLorebook" />}
        </Button>
      ) : null}
      <CapabilityGate capability={SubCapabilityKey.LorebookShareLink}>
        <Button
          iconStart={IconVariants.SHARE}
          variant={ButtonVariant.TEXT}
          onClick={onShowShare}
          aria-label="share"
          data-testid="LorebookProfileActions-ShareButton"
        >
          <Trans i18nKey="common:share" />
        </Button>
      </CapabilityGate>
    </Row>
  );
}

export default function LorebookProfileHeader({ lorebook }: LorebookProfileHeaderProps) {
  const { applicationName } = useApp();
  const { blurNSFW } = useUserSettings();
  const { isAuthenticated } = useUserInfo();
  const { t } = useTranslation();
  const [stateIsShareVisible, setStateIsShareVisible] = useState(false);
  const isMobile = useMediaStore((store) => store.isMobile);
  const lorebookShareUrl = `${window.location.origin}/lorebook/${lorebook.id}`;
  const isAvatarBlurred = (!isAuthenticated || blurNSFW) && lorebook.avatar_is_nsfw;
  const [stateIsModalCharacterOpen, setStateIsModalCharacterOpen] = useState(false);

  // "Attach Lorebook" ships behind the same flag as the rest of the public-lorebook surface,
  // matching native (LorebookDetails), so the whole feature appears and disappears together.
  const { hasFeatureFlag: hasApplyToChatbotFlag } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_DISCOVER_PUBLIC);

  // A lorebook in a restricted moderation state can't be attached, so the CTA reads "Pending Review".
  const isAttachBlocked = useMemo(() => isLorebookAttachBlocked(lorebook), [lorebook]);

  const handleShowShare = useCallback(() => {
    setStateIsShareVisible(true);
  }, []);

  const handleShowCharacterModal = useCallback(() => {
    setStateIsModalCharacterOpen(true);
  }, []);

  const handleCloseCharacterModal = useCallback(() => {
    setStateIsModalCharacterOpen(false);
  }, []);

  return (
    <>
      <PageLayout size={PageSize.Medium}>
        <Row>
          <Col>
            <NavigateBackButtonV2 />
          </Col>
        </Row>
        <Row className="gap-5 flex-col sm:flex-row mt-md">
          <AvatarWithModal src={lorebook.avatar_url}>
            <Image
              src={
                getAvatarUrl(lorebook.avatar_url, {
                  preset: isMobile ? AvatarPreset.AVATAR_400X400 : AvatarPreset.AVATAR_640X640,
                }) || undefined
              }
              alt="avatar image"
              className={`cursor-pointer w-full aspect-[3/4] mob:w-[183px] mob:h-[244px] object-cover rounded-md ${isAvatarBlurred ? "blur sm:blur-lg" : "blur-none"}`}
            />
          </AvatarWithModal>
          <Row className="flex-col gap-5">
            <Col className="gap-1">
              <Text variant={TextVariants.MOBILE_HEADING_3_BOLD}>{lorebook.name}</Text>
              <LocalizedLink className="text-link" to={`/creator/${lorebook.creator_username}`} aria-label="creator-profile">
                <Text variant={TextVariants.LABEL_LARGE_REGULAR}> @{lorebook.creator_username}</Text>
              </LocalizedLink>
            </Col>
            <LorebookProfileActions
              handleShowCharacterModal={handleShowCharacterModal}
              onShowShare={handleShowShare}
              hasApplyToChatbotFlag={hasApplyToChatbotFlag}
              isAttachBlocked={isAttachBlocked}
            />
            <Text variant={TextVariants.LABEL_LARGE_REGULAR}>{lorebook.description}</Text>
            {lorebook.tags?.length ? (
              <Row className="flex-wrap gap-1">
                {lorebook.tags.map((tag) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
              </Row>
            ) : null}
          </Row>
        </Row>
        <CapabilityShareLorebookModal
          visible={stateIsShareVisible}
          title={t("share:lorebook.modal.title")}
          shareUrl={lorebookShareUrl}
          instructions={t("share:lorebook.modal.description")}
          shareMessage={t("share:lorebook.modal.message", {
            lorebookName: lorebook.name,
            applicationName,
            shareUrl: lorebookShareUrl,
          })}
          setShowShare={setStateIsShareVisible}
          characterId={lorebook.id}
          characterName={lorebook.name}
        />
      </PageLayout>
      {stateIsModalCharacterOpen ? (
        <ModalChatbot lorebookId={lorebook.id} isOpen={stateIsModalCharacterOpen} onClose={handleCloseCharacterModal} />
      ) : null}
    </>
  );
}
